<template>
  <div id="reckon-way" class="reckon-way-landing">
    <CultureNav />
    <FeaturedContentCarousel :items="featuredContentItems" />

    <div class="content-container landing-shell">
      <div class="content">
        <v-container>
          <TagFilter
            :value="filterState"
            :entries="searchEntries"
            :tags="searchTags"
            :result-count="filteredEntries.length"
            @input="handleFilterChange"
          />

          <section class="search-results" aria-labelledby="reckon-way-search-results-title">
            <div class="search-results-heading">
              <div>
                <p>Explore the Reckon Way</p>
                <h2 id="reckon-way-search-results-title">{{ resultsHeading }}</h2>
              </div>
              <span role="status" aria-live="polite" aria-atomic="true">
                {{ resultCountLabel }}
              </span>
            </div>

            <div v-if="filteredEntries.length" class="search-results-grid">
              <CultureCard
                v-for="entry in filteredEntries"
                :key="entry.id"
                :title="entry.title"
                :eyebrow="entry.kind"
                :summary="entry.summary"
                :image="entry.image"
                :metadata="entry.metadata"
                :match-labels="matchedLabels(entry)"
                :href="entry.route"
              />
            </div>

            <div v-else class="search-results-empty" role="status" aria-atomic="true">
              <h3>No matching stories</h3>
              <p>
                These filters do not overlap. Remove a filter or switch to Match Any to broaden the results.
              </p>
              <button type="button" @click="clearFilters">Clear All Filters</button>
            </div>
          </section>
        </v-container>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/Footer.vue';
import TagFilter from '@/components/TagFilter.vue';
import FeaturedContentCarousel from '@/components/reckon-way/FeaturedContentCarousel.vue';
import CultureCard from '@/components/reckon-way/culture/CultureCard.vue';
import CultureNav from '@/components/reckon-way/culture/CultureNav.vue';
import { featuredContentItems } from '@/content/culture/featuredContent';
import {
  filterReckonWaySearchEntries,
  reckonWaySearchEntries,
  reckonWaySearchKindSlugs,
  reckonWaySearchTags,
} from '@/content/culture/searchIndex';
import searchUtilities from '@/lib/reckonWaySearch.cjs';

const {
  filtersEqual,
  filtersFromQuery,
  normalizeFilterState,
  queryFromFilters,
} = searchUtilities;

const EMPTY_FILTER_STATE = {
  kindSlugs: [],
  tagSlugs: [],
  matchMode: 'all',
};

function querySignature(query = {}) {
  return Object.keys(query)
    .sort((left, right) => left.localeCompare(right))
    .map((key) => {
      const value = Array.isArray(query[key]) ? query[key].join(',') : query[key];
      return `${key}=${value}`;
    })
    .join('&');
}

export default {
  name: 'ReckonWayLandingView',
  components: {
    AppFooter,
    CultureCard,
    CultureNav,
    FeaturedContentCarousel,
    TagFilter,
  },
  data: () => ({
    featuredContentItems,
    filterState: { ...EMPTY_FILTER_STATE },
    searchEntries: reckonWaySearchEntries,
    searchTags: reckonWaySearchTags,
  }),
  computed: {
    filteredEntries() {
      return filterReckonWaySearchEntries(this.filterState);
    },
    hasActiveFilters() {
      return this.filterState.kindSlugs.length > 0 || this.filterState.tagSlugs.length > 0;
    },
    resultsHeading() {
      return this.hasActiveFilters ? 'Matching Stories' : 'All Stories';
    },
    resultCountLabel() {
      const count = this.filteredEntries.length;
      return `${count} ${count === 1 ? 'story' : 'stories'}`;
    },
    tagLabelsBySlug() {
      return this.searchTags.reduce((labels, tag) => ({
        ...labels,
        [tag.slug]: tag.label,
      }), {});
    },
    validTagSlugs() {
      return this.searchTags.map((tag) => tag.slug);
    },
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.syncFiltersFromRoute();
      },
    },
  },
  methods: {
    handleFilterChange(nextFilters) {
      const normalized = normalizeFilterState(
        nextFilters,
        reckonWaySearchKindSlugs,
        this.validTagSlugs,
      );
      this.filterState = normalized;

      const nextQuery = queryFromFilters(normalized, this.$route.query);
      if (querySignature(nextQuery) === querySignature(this.$route.query)) return;

      this.$router.push({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
      }).catch(() => {});
    },
    syncFiltersFromRoute() {
      const routeFilters = filtersFromQuery(
        this.$route.query,
        reckonWaySearchKindSlugs,
        this.validTagSlugs,
      );
      if (!filtersEqual(this.filterState, routeFilters)) this.filterState = routeFilters;

      const canonicalQuery = queryFromFilters(routeFilters, this.$route.query);
      if (querySignature(canonicalQuery) === querySignature(this.$route.query)) return;

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: canonicalQuery,
      }).catch(() => {});
    },
    matchedLabels(entry) {
      return entry.matchedTagSlugs
        .map((slug) => this.tagLabelsBySlug[slug])
        .filter(Boolean);
    },
    clearFilters() {
      this.handleFilterChange({ ...EMPTY_FILTER_STATE });
    },
  },
};
</script>

<style scoped>
.reckon-way-landing,
.landing-shell {
  background: #050505;
}

.landing-shell {
  padding-bottom: 72px;
  padding-top: 36px;
}

.search-results {
  border-top: 1px solid #252525;
  margin-top: 44px;
  padding-top: 42px;
}

.search-results-heading {
  align-items: flex-end;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.search-results-heading p {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.search-results-heading h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2rem, 5vw, 3rem);
  margin: 0;
}

.search-results-heading > span {
  background: #111;
  border: 1px solid #303030;
  border-radius: 999px;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  padding: 8px 12px;
  text-transform: uppercase;
  white-space: nowrap;
}

.search-results-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.search-results-empty {
  background: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  color: #bdbdbd;
  line-height: 1.6;
  margin: 0;
  padding: 24px;
}

.search-results-empty h3 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.4rem;
  margin: 0;
  text-transform: uppercase;
}

.search-results-empty p {
  margin: 8px 0 18px;
}

.search-results-empty button {
  background: #e83b3b;
  border: 1px solid #e83b3b;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  padding: 10px 14px;
  text-transform: uppercase;
}

@media (max-width: 600px) {
  .landing-shell {
    padding-top: 24px;
  }

  .search-results {
    margin-top: 32px;
    padding-top: 32px;
  }

  .search-results-heading {
    align-items: flex-start;
    display: block;
  }

  .search-results-heading > span {
    display: inline-block;
    margin-top: 12px;
  }
}
</style>
